/**
 * Simple Logger implementation.
 * @module Logger
 */

const fs = require("fs");
const path = require("path");
const mkdirp = require("mkdirp");
const morgan = require("morgan");
const os = require("os");
const { paths } = require("./paths");

/**
 * Normalizes then pads a value under 10 with a leading zero.
 *
 * @private
 * @param {Number} val The value to normalize and pad
 * @returns {Number} A normalized and potentially padded value
 */
function padNormalizedValue(val) {
  const v = Math.abs(Math.floor(val));
  return (v < 10 ? "0" : "") + v;
}

/**
 * Creates a ISO8601-compatible date string, including timezone information.
 * This format does not contain dashes or colons, e.g.:
 * 20160620T132030.392+0200
 *
 * @private
 * @returns {String} A ISO8601 date string.
 */
function getISO8601Date() {
  const date = new Date();
  const offset = -date.getTimezoneOffset();
  const sign = offset >= 0 ? "+" : "-";
  const y = date.getFullYear();
  const m = padNormalizedValue(date.getMonth() + 1);
  const d = padNormalizedValue(date.getDate());
  const hh = padNormalizedValue(date.getHours());
  const mm = padNormalizedValue(date.getMinutes());
  const ss = padNormalizedValue(date.getSeconds());
  const MMM = (date.getMilliseconds() / 1000).toFixed(3).slice(2, 5);

  const dateString = `${y}${m}${d}T${hh}${mm}${ss}.${MMM}`;

  return `${dateString}${sign}${padNormalizedValue( offset / 60 )}${padNormalizedValue( offset % 60 )}`;
}

/**
 * Defines the log level enum
 */
const LogLevel = {
  INFO: 0,
  WARNING: 1,
  ERROR: 2,
  OFF: 3
}

class ScopedLogger {

  /**
   * Creates a new instance of a Logger that logs to the file system.
   * Do not invoke this, use `Logger#get` instead.
   * @constructor
   * @private
   * @param {Object} opts - The logging.get() options
   */
  constructor(opts) {
    this.fileLogger = opts.logger;
    this.fileLogger.scopedLoggers[opts.scope] = this;
    this.scope = opts.scope;
    this.update(opts);
  }

  /**
   * Updates the instance properties to make sure they're up-to-date.
   * @param {Object} opts - The logging.get() options
   */
  update(opts) {
    this.hostname = opts.hostname;
    this.version = opts.version;
    this.logLevel = opts.logLevel || LogLevel.INFO;
  }

  /**
   * Logs an entry. Use the utility functions `Logger#info`,
   * `Logger#warn`, or `Logger#error` instead of this one.
   * @private
   * @param {String} lvl The level/severity, one of "INFO", "WARN", "ERROR".
   * @param {...args} args The values to log.
   */
  log(lvl, args) {
    const message = Array.prototype.slice.call(args);
    const timestamp = getISO8601Date();
    const fileRow = [
      [this.fileLogger.sequenceNo++, timestamp, this.hostname, this.version, this.scope, lvl, message].join("\t")
    ];
    const logRow = ["[%s] [%s] %s: %s", timestamp, this.scope, lvl, message];

    console.log(...logRow); // eslint-disable-line no-console

    fs.writeSync(this.fileLogger.fd, `${fileRow}\r\n`);
  }

  /**
   * Logs a info-level message.
   * Used for debugging/informational messages, also known as "trace".
   *
   * @param {...args} The values to log.
   */
  info(...args) {
    if (this.logLevel <= LogLevel.INFO) {
      this.log("INFO", args);
    }
  }

  /**
   * Logs a warning-level message.
   * Used when something semi-unexpected happen, that could impact performance or
   * similar.
   *
   * @param {...args} The values to log.
   */
  warn(...args) {
    if (this.logLevel <= LogLevel.WARNING) {
      this.log("WARN", args);
    }
  }

  /**
   * Logs error-level message.
   * Reserved for messages that will be followed by restarting the server because
   * we cannot resume operations.
   *
   * @param {...args} The values to log.
   */
  error(...args) {
    if (this.logLevel <= LogLevel.ERROR) {
      this.log("ERROR", args);
    }
  }
  
  /**
   * Gets the log level
   * @returns {number} Log level
   */
  getLogLevel() {
    return this.logLevel;
  }

  /**
   * Close down logger.
   * @param {String} reason - String explaining the reason for closing.
   */
  close(reason) {
    if (reason) {
      this.info(reason);
    }
    this.fileLogger.close();
  }

  injectLogger(app, skip) {
    this.fileLogger.injectLogger(app, skip);
  }
}

const loggers = {};

class Logger {

  /**
   * Creates a new instance of a Logger that logs to the file system.
   * Do not invoke this, use `Logger#get` instead.
   * @constructor
   * @private
   * @param {Object} opts - The logging.get() options
   */
  constructor(opts) {
    const {
      name,
      logPath
    } = opts;
    this.name = name;
    const logPrefix = path.resolve(logPath, getISO8601Date().replace(/[^0-9]/g, ""));
    this.logFile = `${logPrefix}.log`;
    this.accessLogFile = `${logPrefix}-access.log`;
    this.openFiles();
    this.scopedLoggers = {};
    this.sequenceNo = 1;
  }

  /**
   * @private
   */
  openFiles() {
    mkdirp.sync(path.dirname(this.logFile));
    this.fd = fs.openSync(this.logFile, "w+");
    fs.writeSync(this.fd, "Sequence#\tTimestamp\tHostname\tVersion\tLogger\tSeverity\tMessage\r\n");
  }

  /**
   * Configures an express.js morgan logger for console/file logging.
   * @param {Object} app The express app to inject into
   */
  injectLogger(app, skip) {
    if (this.morganFd) {
      throw new Error("this logger has already been used with injectLogger");
    }

    this.morganFd = fs.openSync(this.accessLogFile, "a");
    const accessLogStream = fs.createWriteStream("", {
      fd: this.morganFd
    });
    app.use(morgan("combined", {
      stream: accessLogStream,
      skip: skip,
    }));
    app.use(morgan("dev"));
  }

  /**
   * Close down logger stream and delete the logger.
   */
  close() {
    if (this.fd) {
      fs.closeSync(this.fd);
    }
    if (this.morganFd) {
      fs.closeSync(this.morganFd);
    }
    delete loggers[this.name];
  }

  getScopedLogger(opts) {
    opts.logger = this;
    const logger = this.scopedLoggers[opts.scope] || new ScopedLogger(opts);
    logger.update(opts);
    return logger;
  }
}

const defaultLogPath = path.resolve(paths.senseDataPath, "Log");

/**
 * Checks if the execution process has write permissions on a given path.
 * @param {String} logPath - Path to test
 * @returns {Boolean} true if can write, false otherwise.
 */
function isWritable(logPath) {
  try {
    mkdirp.sync(logPath);
    const testFile = path.resolve(logPath, ".test");
    fs.writeFileSync(testFile, 'test');
    fs.unlinkSync(testFile);
    return true;
  } catch (err) {
    return false;
  }
}

const logging = {

  /**
   * Get an instance of a logger for a given name.
   * @param {Object} opts - The logger options.
   * @param {String} opts.name - The name of the Logger.
   * @param {String} opts.hostname - The hostname the service is using
   * @param {String} opts.version - The exact version of the component that is running
   * @param {String} [opts.scope] - This logger's scoped name that appears in the rows of the logs. Defaults to "Global".
   * @param {String} [opts.logPath] - The path to where this logger should put the log files.
   *	If omitted a default path containing the name parameter will be used.
   * @throws Will throw an error if the required parameters is not given.
   * @returns {Logger} A logger instance.
   */
  get(opts) {
    const {
      name,
      hostname,
      version,
    } = opts;

    if (!name || typeof name !== "string") {
      throw new Error("get function has to be called with a name{string} parameter");
    }

    if (!hostname || typeof hostname !== "string") {
      throw new Error("get function has to be called with a hostname{string} parameter");
    }

    if (!version || typeof version !== "string") {
      throw new Error("get function has to be called with a version{string} parameter");
    }

    opts.logPath = path.resolve(opts.logPath || defaultLogPath, name);

    opts.scope = opts.scope || "Global";
    if (!loggers[name]) {
      if (!isWritable(opts.logPath)) {
        opts.logPath = path.resolve(os.tmpdir(), "qlik");
      }
      loggers[name] = new Logger(opts);
    }

    return loggers[name].getScopedLogger(opts);
  }
};

module.exports = {
  logging,
  Logger,
  LogLevel,
  getISO8601Date
};
